/**
 * Platform staff + test accounts (owner decision 2026-10-15, follow-up to OP#1859 / MR !265).
 *
 * Pure helpers for the "Platform staff" section of the Team screen, kept free of React so
 * `npm run test:unit` (node --test) can pin them.
 *
 * WHY THE SECTION EXISTS: !265 narrowed the Team list to the customer selected in the header,
 * so GTK staff who hold NO customer (platform admins, legacy org-less accounts) appeared on no
 * Team page at all. A platform admin now sees them under "Platform staff".
 *
 * TEST ACCOUNTS (server: Keycloak group "meridian-test-accounts", `is_test` on each row) are
 * never listed on a customer's Team page. They are listed here, with a "Test" badge. The flag
 * is display-only: it changes no role, customer or site.
 *
 * Every rule here MIRRORS the server (api/v2/operators.py); the server is the authority.
 */

/** The fields of an operator these helpers read. */
export interface StaffLike {
  org_ids?: ReadonlyArray<number | string> | null;
  is_test?: boolean | null;
  enabled?: boolean;
  username?: string;
}

/** Mirrors operators._is_platform_staff_row: no customer, or flagged as a test account. */
export function isPlatformStaffRow(op: StaffLike): boolean {
  return (op.org_ids ?? []).length === 0 || !!op.is_test;
}

/**
 * The scope line under a Platform staff name: "No customer" for real GTK staff, otherwise the
 * customer names (a test account keeps its customers -- the retest personas live in one).
 * More than two customers collapse to "A, B +N".
 */
export function staffScopeLabel(
  op: StaffLike,
  orgName: (id: number | string) => string,
): string {
  const ids = op.org_ids ?? [];
  if (ids.length === 0) return "No customer";
  const names = ids.map(orgName).sort();
  return names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

/** The label of the test-flag action for a row, given its current state. */
export function testToggleLabel(op: StaffLike): string {
  return op.is_test ? "Not a test account" : "Mark as test account";
}

/** The body PUT /operators/{id}/test-account expects to flip the current state. */
export function testToggleBody(op: StaffLike): { test: boolean } {
  return { test: !op.is_test };
}

/** The label of the sign-in toggle (Platform staff rows only), given its current state. */
export function signInToggleLabel(op: StaffLike): string {
  return op.enabled === false ? "Turn on sign-in" : "Turn off sign-in";
}

/**
 * Roles offered when ADDING platform staff. Only platform_admin: every other role is
 * customer-scoped and the server refuses it without a customer ("at least one org is
 * required"). Someone who should work inside customers is added from that customer's Team page.
 */
export const PLATFORM_STAFF_CREATE_ROLES = ["platform_admin"] as const;

/** Real staff first, then test accounts; alphabetical inside each group (matches the server). */
export function sortStaff<T extends StaffLike>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const t = Number(!!a.is_test) - Number(!!b.is_test);
    if (t !== 0) return t;
    return (a.username ?? "").toLowerCase().localeCompare((b.username ?? "").toLowerCase());
  });
}
